import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit, computed } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-large-shareholder-category-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './large-shareholder-category-list.component.html',
  styleUrl: './large-shareholder-category-list.component.scss'
})
export class LargeShareholderCategoryListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  largeShareholderCategories = signal<LargeShareholderCategory[]>([]);
  isSearchVisible = false;

  // Computed columns that update when categories change
  largeShareholderCategoriesColumns = computed<ColumnConfig[]>(() => {
    const parentCategories = this.largeShareholderCategories().filter(c => c.parentId === null);
    return [
      { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
      {
        key: 'parentId',
        header: 'Parent',
        type: 'select',
        sortable: true,
        editable: true,
        selectOptions: [
          { value: null as any, label: '(None)' },
          ...parentCategories.map(c => ({ value: c.id, label: c.name }))
        ]
      },
      { key: 'rank', header: 'Rank', type: 'text', sortable: true, editable: true }
    ];
  });

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadLargeShareholderCategories();
  }

  loadLargeShareholderCategories(): void {
    this.spinnerService.show();
    this.lutService.getLargeShareholderCategories().subscribe({
      next: (data) => {
        const sortedData = this.sortCategories(data);
        this.largeShareholderCategories.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading large shareholder categories:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load Large Shareholder Categories:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  private sortCategories(categories: LargeShareholderCategory[]): LargeShareholderCategory[] {
    return [...categories].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateLargeShareholderCategory(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onItemSelected(category: LargeShareholderCategory): void {
  }

  onEditLargeShareholderCategory(category: LargeShareholderCategory): void {
  }

  onDeleteLargeShareholderCategory(category: LargeShareholderCategory): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(category.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCategories = this.largeShareholderCategories();

          // 1. Optimistically remove from local state immediately
          this.largeShareholderCategories.update(categories => categories.filter(cat => cat.id !== category.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteLargeShareholderCategory(category.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Large shareholder category deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting large shareholder category:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.largeShareholderCategories.set(originalCategories);
              this.toast.error(`<strong>Failed to delete Large Shareholder Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: LargeShareholderCategory, newName: string} | {item: LargeShareholderCategory, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: LargeShareholderCategory;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      if (event.columnKey === 'parentId') {
        // When updating parentId, also update parentName
        const newParentId = event.newValue;
        let newParentName: string | null = null;

        if (newParentId) {
          const parent = this.largeShareholderCategories().find(c => c.id === newParentId);
          newParentName = parent ? parent.name : null;
        }

        updatedItem = {
          ...event.item,
          parentId: newParentId,
          parentName: newParentName
        };
      } else {
        updatedItem = { ...event.item, [event.columnKey]: event.newValue };
      }
    }

    // 1. Optimistically update local state immediately and re-sort
    this.largeShareholderCategories.update(categories => {
      const updated = categories.map(cat => cat.id === event.item.id ? updatedItem : cat);
      return this.sortCategories(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateLargeShareholderCategory(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Large shareholder category updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.largeShareholderCategories.update(categories => {
          const updated = categories.map(cat => cat.id === event.item.id ? response : cat);
          return this.sortCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error updating large shareholder category:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.largeShareholderCategories.update(categories =>
          categories.map(cat => cat.id === event.item.id ? originalItem : cat)
        );
        this.toast.error(`<strong>Failed to update Large Shareholder Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();

    // Get parentId and parentName from the event
    const parentId = event['parentId'] || null;
    let parentName: string | null = null;

    // If a parent was selected, find its name
    if (parentId) {
      const parent = this.largeShareholderCategories().find(c => c.id === parentId);
      parentName = parent ? parent.name : null;
    }

    const newCategory: LargeShareholderCategory = {
      id: tempId,
      name: event['name'],
      rank: event['rank'] || 0,
      parentId: parentId,
      parentName: parentName,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.largeShareholderCategories.update(categories => this.sortCategories([...categories, newCategory]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createLargeShareholderCategory({ name: event['name'], rank: event['rank'] || 0, parentId: parentId, parentName: parentName, systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Large shareholder category created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.largeShareholderCategories.update(categories => {
          const updated = categories.map(cat => cat.id === tempId ? response : cat);
          return this.sortCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error creating large shareholder category:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.largeShareholderCategories.update(categories =>
          categories.filter(cat => cat.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Large Shareholder Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.spinnerService.show();
    this.lutService.getLargeShareholderCategories().subscribe({
      next: (data) => {
        let filteredData = data;
        if (searchFilter.name) {
          filteredData = data.filter(cat => cat.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? ''));
        }
        const sortedData = this.sortCategories(filteredData);
        this.largeShareholderCategories.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading large shareholder categories:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load Large Shareholder Categories:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
